/**
 * Mock Andes Access Service for StarNexus platform
 * Handles permission requests and subscriptions for Andes tables
 * @module services/starnexus/mock/mockAndesAccessService
 */

import type {
  PermissionRequest,
  Subscription,
  SubscriptionTarget,
  CreatePermissionRequestInput,
  CreateSubscriptionInput,
  GetPermissionRequestsParams,
  GetSubscriptionsParams,
  PermissionStatus
} from '../../../types/starnexus/andesAccess';
import { mockPermissionRequests } from '../../../data/starnexus/mockPermissionRequests';
import { mockSubscriptions } from '../../../data/starnexus/mockSubscriptions';
import { mockSubscriptionTargets } from '../../../data/starnexus/mockSubscriptionTargets';

// Simulated latency helper
const delay = (ms: number = 300): Promise<void> => new Promise((resolve) => setTimeout(resolve, 200 + Math.random() * ms));

// In-memory stores for mutations
let permissionRequests = [...mockPermissionRequests];
let subscriptions = [...mockSubscriptions];
let subscriptionTargets = [...mockSubscriptionTargets];

export const mockAndesAccessService = {
  // ============================================================================
  // Permission Requests
  // ============================================================================

  async getPermissionRequests(params?: GetPermissionRequestsParams): Promise<PermissionRequest[]> {
    await delay();

    let result = [...permissionRequests];

    if (params) {
      if (params.status && params.status !== 'all') {
        result = result.filter((r) => r.status === params.status);
      }
      if (params.searchText) {
        const search = params.searchText.toLowerCase();
        result = result.filter(
          (r) =>
            r.datasetName.toLowerCase().includes(search) ||
            r.providerName.toLowerCase().includes(search) ||
            r.identityValue.toLowerCase().includes(search) ||
            r.tableName.toLowerCase().includes(search)
        );
      }
      if (params.datasetId) {
        result = result.filter((r) => r.datasetId === params.datasetId);
      }
      if (params.identityType) {
        result = result.filter((r) => r.identityType === params.identityType);
      }
    }

    // Sort by requestedAt descending (newest first)
    result.sort((a, b) => b.requestedAt.getTime() - a.requestedAt.getTime());

    return result;
  },

  async getPermissionRequest(requestId: string): Promise<PermissionRequest | null> {
    await delay();
    return permissionRequests.find((r) => r.requestId === requestId) || null;
  },

  async createPermissionRequest(input: CreatePermissionRequestInput, requestedBy: string = 'yyangzam'): Promise<PermissionRequest[]> {
    await delay(500);

    const createdRequests: PermissionRequest[] = [];

    for (const datasetId of input.datasetIds) {
      // Find dataset info (mock lookup)
      const existingRequest = permissionRequests.find((r) => r.datasetId === datasetId);

      const newRequest: PermissionRequest = {
        requestId: `pr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        datasetId,
        datasetName: existingRequest?.datasetName || `dataset_${datasetId}`,
        providerId: existingRequest?.providerId || 'mock-provider-id',
        providerName: existingRequest?.providerName || 'mock_provider',
        tableName: existingRequest?.tableName || `table_${datasetId}`,
        tableVersion: existingRequest?.tableVersion || 1,
        identityType: input.identityType,
        identityValue: input.identityValue,
        businessJustificationType: input.businessJustificationType,
        businessJustification: input.businessJustification,
        status: 'pending',
        requestedBy,
        requestedAt: new Date()
      };

      permissionRequests.push(newRequest);
      createdRequests.push(newRequest);
    }

    return createdRequests;
  },

  async updatePermissionRequestStatus(requestId: string, status: PermissionStatus, reviewedBy: string = 'admin', reviewNotes?: string): Promise<PermissionRequest | null> {
    await delay();

    const index = permissionRequests.findIndex((r) => r.requestId === requestId);
    if (index === -1) return null;

    permissionRequests[index] = {
      ...permissionRequests[index],
      status,
      reviewedBy,
      reviewedAt: new Date(),
      reviewNotes
    };

    return permissionRequests[index];
  },

  async bulkUpdatePermissionStatus(requestIds: string[], status: PermissionStatus, reviewedBy: string = 'admin'): Promise<{ success: string[]; failed: string[] }> {
    await delay(500);

    const success: string[] = [];
    const failed: string[] = [];

    for (const id of requestIds) {
      const result = await this.updatePermissionRequestStatus(id, status, reviewedBy);
      if (result) {
        success.push(id);
      } else {
        failed.push(id);
      }
    }

    return { success, failed };
  },

  // ============================================================================
  // Subscriptions
  // ============================================================================

  async getSubscriptions(params?: GetSubscriptionsParams): Promise<Subscription[]> {
    await delay();

    let result = [...subscriptions];

    if (params) {
      if (params.targetType && params.targetType !== 'all') {
        result = result.filter((s) => s.targetType === params.targetType);
      }
      if (params.status && params.status !== 'all') {
        result = result.filter((s) => s.status === params.status);
      }
      if (params.searchText) {
        const search = params.searchText.toLowerCase();
        result = result.filter(
          (s) =>
            s.datasetName.toLowerCase().includes(search) || s.providerName.toLowerCase().includes(search) || s.targetName.toLowerCase().includes(search) || s.tableName.toLowerCase().includes(search)
        );
      }
      if (params.datasetId) {
        result = result.filter((s) => s.datasetId === params.datasetId);
      }
    }

    // Sort by createdAt descending (newest first)
    result.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

    return result;
  },

  async getSubscription(subscriptionId: string): Promise<Subscription | null> {
    await delay();
    return subscriptions.find((s) => s.subscriptionId === subscriptionId) || null;
  },

  async createSubscription(input: CreateSubscriptionInput, createdBy: string = 'yyangzam'): Promise<Subscription[]> {
    await delay(500);

    const target = subscriptionTargets.find((t) => t.targetId === input.targetId);
    if (!target) {
      throw new Error(`Subscription target not found: ${input.targetId}`);
    }

    const createdSubscriptions: Subscription[] = [];

    for (const datasetId of input.datasetIds) {
      // Find dataset info (mock lookup)
      const existingSub = subscriptions.find((s) => s.datasetId === datasetId);

      const newSubscription: Subscription = {
        subscriptionId: `sub-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        datasetId,
        datasetName: existingSub?.datasetName || `dataset_${datasetId}`,
        providerId: existingSub?.providerId || 'mock-provider-id',
        providerName: existingSub?.providerName || 'mock_provider',
        tableName: existingSub?.tableName || `table_${datasetId}`,
        tableVersion: existingSub?.tableVersion || 1,
        targetId: target.targetId,
        targetName: target.targetName,
        targetType: target.targetType,
        region: input.region,
        versionMigrationStrategy: input.versionMigrationStrategy,
        status: 'pending',
        description: input.description,
        createdBy,
        createdAt: new Date()
      };

      subscriptions.push(newSubscription);
      createdSubscriptions.push(newSubscription);
    }

    return createdSubscriptions;
  },

  async deleteSubscription(subscriptionId: string): Promise<boolean> {
    await delay();

    const index = subscriptions.findIndex((s) => s.subscriptionId === subscriptionId);
    if (index === -1) return false;

    // Mark as deleted instead of removing
    subscriptions[index] = {
      ...subscriptions[index],
      status: 'deleted'
    };

    return true;
  },

  async bulkDeleteSubscriptions(subscriptionIds: string[]): Promise<{ success: string[]; failed: string[] }> {
    await delay(500);

    const success: string[] = [];
    const failed: string[] = [];

    for (const id of subscriptionIds) {
      const result = await this.deleteSubscription(id);
      if (result) {
        success.push(id);
      } else {
        failed.push(id);
      }
    }

    return { success, failed };
  },

  // ============================================================================
  // Subscription Targets (Admin)
  // ============================================================================

  async getSubscriptionTargets(): Promise<SubscriptionTarget[]> {
    await delay();
    return [...subscriptionTargets];
  },

  async getSubscriptionTarget(targetId: string): Promise<SubscriptionTarget | null> {
    await delay();
    return subscriptionTargets.find((t) => t.targetId === targetId) || null;
  },

  async createSubscriptionTarget(target: Omit<SubscriptionTarget, 'targetId'>): Promise<SubscriptionTarget> {
    await delay();

    const newTarget: SubscriptionTarget = {
      ...target,
      targetId: `target-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };

    subscriptionTargets.push(newTarget);
    return newTarget;
  },

  async updateSubscriptionTarget(targetId: string, updates: Partial<Omit<SubscriptionTarget, 'targetId'>>): Promise<SubscriptionTarget | null> {
    await delay();

    const index = subscriptionTargets.findIndex((t) => t.targetId === targetId);
    if (index === -1) return null;

    subscriptionTargets[index] = {
      ...subscriptionTargets[index],
      ...updates
    };

    return subscriptionTargets[index];
  },

  async deleteSubscriptionTarget(targetId: string): Promise<boolean> {
    await delay();

    // Check if target is in use
    const inUse = subscriptions.some((s) => s.targetId === targetId && s.status !== 'deleted');
    if (inUse) {
      throw new Error('Cannot delete target: currently in use by active subscriptions');
    }

    const index = subscriptionTargets.findIndex((t) => t.targetId === targetId);
    if (index === -1) return false;

    subscriptionTargets.splice(index, 1);
    return true;
  },

  // ============================================================================
  // Utility Methods
  // ============================================================================

  // Get counts for dashboard
  async getCounts(): Promise<{
    pendingPermissions: number;
    activeSubscriptions: number;
    failedSubscriptions: number;
  }> {
    await delay(100);

    return {
      pendingPermissions: permissionRequests.filter((r) => r.status === 'pending').length,
      activeSubscriptions: subscriptions.filter((s) => s.status === 'active').length,
      failedSubscriptions: subscriptions.filter((s) => s.status === 'failed').length
    };
  },

  // Reset to initial state (for testing)
  reset(): void {
    permissionRequests = [...mockPermissionRequests];
    subscriptions = [...mockSubscriptions];
    subscriptionTargets = [...mockSubscriptionTargets];
  }
};
